// ============================================================================
// TERMS OF SERVICE PAGE - Simple UAE-compliant terms
// ============================================================================

import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import Logo from '../components/Logo';

const Terms = () => {
  const { i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';

  return (
    <div className="min-h-screen bg-gray-50" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link 
            to="/" 
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} />
            Back
          </Link>
          <Logo variant="light" size="small" />
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 md:p-12">
          <h1 className="font-serif text-4xl font-bold text-gray-900 mb-2">Terms of Service</h1>
          <p className="text-gray-500 mb-8">Last updated: January 2025</p>

          <div className="prose prose-gray max-w-none">
            <section className="mb-8">
              <h2 className="font-serif text-2xl font-bold text-gray-900 mb-4">1. Agreement to Terms</h2>
              <p className="text-gray-600">
                By accessing or using Lab2040's AI readiness assessment platform ("Service"), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our Service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-serif text-2xl font-bold text-gray-900 mb-4">2. Description of Service</h2>
              <p className="text-gray-600 mb-4">
                Lab2040 provides an AI career readiness assessment tool that helps users understand their potential exposure to AI-driven changes in the workforce. Our Service includes:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 ms-4">
                <li>AI disruption risk assessment questionnaires</li>
                <li>Personalized risk scores and analysis</li>
                <li>Career recommendations and action plans</li>
                <li>Progress tracking over time</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-serif text-2xl font-bold text-gray-900 mb-4">3. User Accounts</h2>
              <p className="text-gray-600 mb-4">
                To access certain features, you may need to create an account. You agree to:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 ms-4">
                <li>Provide accurate and complete information</li>
                <li>Maintain the security of your account credentials</li>
                <li>Notify us immediately of any unauthorized access</li>
                <li>Accept responsibility for all activities under your account</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-serif text-2xl font-bold text-gray-900 mb-4">4. Subscriptions and Payments</h2>
              <p className="text-gray-600 mb-4">
                Lab2040 offers both free and paid subscription plans:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 ms-4">
                <li><strong>Free Plan:</strong> Basic assessment and general recommendations</li>
                <li><strong>Pro Plan:</strong> Full reports, detailed action plans, and premium features</li>
              </ul>
              <p className="text-gray-600 mt-4">
                Paid subscriptions are billed monthly. You may cancel at any time, and your subscription will remain active until the end of the current billing period. Refunds are provided in accordance with applicable consumer protection laws.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-serif text-2xl font-bold text-gray-900 mb-4">5. Acceptable Use</h2>
              <p className="text-gray-600 mb-4">You agree not to:</p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 ms-4">
                <li>Use the Service for any unlawful purpose</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Interfere with or disrupt the Service</li>
                <li>Share your account with others or create multiple accounts</li>
                <li>Reproduce, distribute, or resell any part of our Service</li>
                <li>Use automated systems to access the Service without permission</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-serif text-2xl font-bold text-gray-900 mb-4">6. Intellectual Property</h2>
              <p className="text-gray-600">
                All content, features, and functionality of the Service, including but not limited to text, graphics, logos, and software, are owned by Lab2040 and protected by intellectual property laws. You may not copy, modify, or distribute any part of our Service without prior written consent.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-serif text-2xl font-bold text-gray-900 mb-4">7. Disclaimer</h2>
              <p className="text-gray-600 mb-4">
                The assessments and recommendations provided by Lab2040 are for informational purposes only. They are based on general research and analysis, and should not be considered as:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 ms-4">
                <li>Professional career advice</li>
                <li>Guaranteed predictions of future employment outcomes</li>
                <li>Financial or investment advice</li>
              </ul>
              <p className="text-gray-600 mt-4">
                We encourage users to consult with qualified professionals for personalized career guidance.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-serif text-2xl font-bold text-gray-900 mb-4">8. Limitation of Liability</h2>
              <p className="text-gray-600">
                To the maximum extent permitted by law, Lab2040 shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of or inability to use the Service. Our total liability shall not exceed the amount you paid us in the twelve months preceding the claim.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-serif text-2xl font-bold text-gray-900 mb-4">9. Termination</h2>
              <p className="text-gray-600">
                We reserve the right to suspend or terminate your access to the Service at any time, with or without cause, with or without notice. Upon termination, your right to use the Service will immediately cease. You may also terminate your account at any time by contacting us.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-serif text-2xl font-bold text-gray-900 mb-4">10. Governing Law</h2>
              <p className="text-gray-600">
                These Terms shall be governed by and construed in accordance with the laws of the United Arab Emirates. Any disputes arising from these Terms or your use of the Service shall be subject to the exclusive jurisdiction of the courts of Dubai, UAE.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-serif text-2xl font-bold text-gray-900 mb-4">11. Changes to Terms</h2>
              <p className="text-gray-600">
                We may modify these Terms at any time. We will notify users of significant changes by posting the updated terms on our website. Your continued use of the Service after changes are posted constitutes your acceptance of the modified Terms.
              </p>
            </section>

            <section>
              <h2 className="font-serif text-2xl font-bold text-gray-900 mb-4">12. Contact Us</h2>
              <p className="text-gray-600">
                If you have any questions about these Terms of Service, please contact us at:
              </p>
              <p className="text-gray-900 font-medium mt-4">
                Email: <a href="mailto:support@lab2040.ai" className="text-teal-600 hover:text-teal-700">support@lab2040.ai</a>
              </p>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Terms;
